import type {
	JiraSideNav,
	JiraSideNavMessageMetadata,
} from '@post-office/placement-create-pipeline/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-create-pipeline/create-pipeline';
import { type GenerateValidationFor } from '@atlassian/vindicated';

import {
	type VARIANT_CLI,
	type VARIANT_CODE_REVIEW,
	type VARIANT_CLI_AND_CODE_REVIEW,
	type MessageTemplateContext,
} from '../../..';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { type AnyHydrationResult } from '@post-office/shared-contracts/placement-pipe/pipeline-types';
import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import { type JSX } from 'react/jsx-runtime';

export type VariantType =
	| typeof VARIANT_CLI
	| typeof VARIANT_CODE_REVIEW
	| typeof VARIANT_CLI_AND_CODE_REVIEW
	| null;

export type HydrationResult = {
	variantType: VariantType;
	tenantId: string;
	accountId: string;
};

export type VHydrationResult = GenerateValidationFor<HydrationResult>;

type Pipe = JiraSideNav<{
	messageContext: MessageTemplateContext;
	hydrationResult: HydrationResult;
}>;

const clientPipeline = createClientPipeline<Pipe>();
export const createRender: <
	U extends (
		renderProps: Omit<HydrationResult, 'postOffice'> & {
			onDismissed: () => void;
		},
	) => JSX.Element | null,
>(
	render: U,
) => (
	renderProps: Omit<HydrationResult, 'postOffice'> & {
		onDismissed: () => void;
	},
) => JSX.Element | null = clientPipeline.createRender;
export const createValidate: (
	validate: (apiResponse: unknown) => HydrationResult,
) => (apiResponse: unknown) => HydrationResult = clientPipeline.createValidate;
const serverPipeline = createServerPipeline<Pipe>();
export const createHydrate: (
	hydrate: (
		requestContext: RequestContext,
		placementContext: Record<string, never>,
		messageContext: Record<string, never> & {
			messageInstanceId: string;
			eventTime: string;
		},
	) => Promise<HydrationResult>,
) => (
	requestContext: RequestContext,
	placementContext: Record<string, never>,
	messageContext: Record<string, never> & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Promise<AnyHydrationResult> = serverPipeline.createHydrate;
export const createHydrateExample: (
	hydrateExample: (
		requestContext: Partial<RequestContext>,
		placementContext: Record<string, never>,
		messageContext: Record<string, never>,
	) => HydrationResult,
	config: {
		requestContext?: Partial<RequestContext>;
		additionalRenderProps: Omit<
			Omit<HydrationResult, 'postOffice'> & {
				onDismissed: () => void;
			},
			keyof HydrationResult
		>;
	},
) => (
	requestContext: Partial<RequestContext>,
	placementContext: Record<string, never>,
	messageContext: Record<string, never> & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Omit<HydrationResult, 'postOffice'> & {
	onDismissed: () => void;
} = serverPipeline.createHydrateExample;
export const createTransformMetadata: (
	transformMetadata: (params: {
		messageContext: Record<string, never>;
	}) => JiraSideNavMessageMetadata,
) => (params: { messageContext: Record<string, never> }) => TransformMetadataResult =
	serverPipeline.createTransformMetadata;
